import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { AsideSection } from "../misc/AsideSection";
import type { Company } from "../types";
import { ChannelBadge } from "../visitor-insights/ChannelBadge";
import { fetchLeadboxerCompanyLeads } from "../visitor-insights/leadboxer/api";
import { LEADBOXER_BRANDS } from "../visitor-insights/leadboxer/instances";
import type {
  CompanyMatchedLead,
  LeadMatchReason,
  LeadboxerInstance,
} from "../visitor-insights/leadboxer/types";
import {
  getLastActivityTimestamp,
  getLeadEvents,
  getLeadVisits,
} from "../visitor-insights/leadboxer/utils";
import { VisitorLeadModal } from "../visitor-insights/VisitorLeadModal";

const COMPANY_ACTIVITY_PERIODS = [7, 30] as const;

function formatLastActivity(lead: CompanyMatchedLead): string {
  const pretty = lead.prettyLastEvent;
  if (pretty && typeof pretty === "string") {
    const ts = getLastActivityTimestamp(lead);
    if (ts) {
      return formatDistanceToNow(new Date(ts), { addSuffix: true });
    }
    return pretty;
  }
  return "-";
}

function getBrandLabel(
  instance: LeadboxerInstance,
  translate: (key: string, options?: { _: string }) => string,
): string {
  const brand = LEADBOXER_BRANDS.find((b) => b.id === instance);
  if (!brand) return instance;
  return translate(brand.labelKey, { _: brand.fallback });
}

function getMatchReasonLabel(
  reason: LeadMatchReason,
  translate: (key: string, options?: { _: string }) => string,
): string {
  const labels: Record<LeadMatchReason, string> = {
    domain: translate("crm.recent_website_activity.match_reason.domain", {
      _: "Matched by domain",
    }),
    domain_alias: translate(
      "crm.recent_website_activity.match_reason.domain_alias",
      { _: "Matched by domain alias" },
    ),
    email_domain: translate(
      "crm.recent_website_activity.match_reason.email_domain",
      { _: "Matched by email domain" },
    ),
    linkedin: translate("crm.recent_website_activity.match_reason.linkedin", {
      _: "Matched by LinkedIn",
    }),
    name: translate("crm.recent_website_activity.match_reason.name", {
      _: "Matched by name",
    }),
  };
  return labels[reason];
}

export const CompanyRecentWebsiteActivity = ({
  record,
}: {
  record: Company;
}) => {
  const translate = useTranslate();
  const [period, setPeriod] = useState<number>(30);
  const [selectedLead, setSelectedLead] = useState<CompanyMatchedLead | null>(
    null,
  );

  const isDemo = import.meta.env.VITE_IS_DEMO === "true";

  const { data: leads = [], isPending, error } = useQuery({
    queryKey: ["recent-website-activity", record.id, period],
    queryFn: () => fetchLeadboxerCompanyLeads(Number(record.id), period),
    enabled: !isDemo && !!record.id,
    staleTime: 10 * 60 * 1000,
  });

  if (isDemo) {
    return (
      <AsideSection title={translate("crm.recent_website_activity.title", {
        _: "Recent website activity",
      })}>
        <p className="text-muted-foreground text-xs">
          {translate("crm.recent_website_activity.demo_unavailable", {
            _: "Recent website activity requires Leadboxer configuration and is not available in demo mode.",
          })}
        </p>
      </AsideSection>
    );
  }

  const visibleLeads = leads.slice(0, 5);
  const topLead = leads[0];

  return (
    <AsideSection
      title={translate("crm.recent_website_activity.title", {
        _: "Recent website activity",
      })}
      noGap
    >
      <div className="flex flex-wrap gap-1 pb-2">
        {COMPANY_ACTIVITY_PERIODS.map((days) => (
          <Button
            key={days}
            type="button"
            size="sm"
            variant={period === days ? "default" : "outline"}
            className="h-7 text-xs"
            onClick={() => setPeriod(days)}
          >
            {days === 7
              ? translate("crm.visitor_insights.period.last_7d", {
                  _: "Last 7 Days",
                })
              : translate("crm.visitor_insights.period.last_30d", {
                  _: "Last 30 Days",
                })}
          </Button>
        ))}
      </div>

      {isPending ? (
        <div className="flex items-center gap-2 py-2 text-muted-foreground text-xs">
          <Spinner className="size-3" />
          <span>
            {translate("crm.recent_website_activity.loading", {
              _: "Loading...",
            })}
          </span>
        </div>
      ) : null}

      {error ? (
        <p className="text-destructive text-xs py-1">
          {error instanceof Error ? error.message : "Failed to load activity"}
        </p>
      ) : null}

      {!isPending && !error && leads.length === 0 ? (
        <p className="text-muted-foreground text-xs py-1">
          {translate("crm.recent_website_activity.empty", {
            _: "No recent website activity found",
          })}
        </p>
      ) : null}

      {!isPending && !error && topLead ? (
        <div className="text-xs text-muted-foreground pb-2 space-y-0.5">
          <div>
            {translate("crm.visitor_insights.columns.last_activity", {
              _: "Last Activity",
            })}
            : {formatLastActivity(topLead)}
          </div>
          <div>
            {translate("crm.recent_website_activity.summary", {
              visits: String(getLeadVisits(topLead)),
              events: String(getLeadEvents(topLead)),
              count: leads.length,
              _: "%{count} visits · %{visits} sessions · %{events} page views (latest)",
            })}
          </div>
        </div>
      ) : null}

      {!isPending && !error && visibleLeads.length > 0 ? (
        <ul className="space-y-2">
          {visibleLeads.map((lead) => (
            <li key={`${lead.instance}-${lead["lead card link"] ?? lead.machine_id}`}>
              <button
                type="button"
                className="w-full text-left rounded-md border px-3 py-2 hover:bg-muted/50 transition-colors"
                onClick={() => setSelectedLead(lead)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-xs truncate">
                    {getBrandLabel(lead.instance, translate)}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatLastActivity(lead)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <ChannelBadge channel={String(lead.last_channel ?? "-")} />
                  <span className="text-[10px] text-muted-foreground truncate">
                    {getMatchReasonLabel(lead.matchReason, translate)}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <VisitorLeadModal
        lead={selectedLead}
        instance={selectedLead?.instance ?? "gobo-dk"}
        open={!!selectedLead}
        onOpenChange={(open) => {
          if (!open) setSelectedLead(null);
        }}
      />
    </AsideSection>
  );
};
